import React, { useEffect, useState } from "react"
import { Button, Card, CardBody, CardTitle, Col, Input, Label, Row } from "reactstrap"
import { Formik, Form, Field, ErrorMessage } from "lib/formik"
import { useNavigate } from "react-router-dom"
import { walletSettingsSchema } from "components/Common/validation"
import Breadcrumbs from "components/Common/Breadcrumb"
import { useSelector, useDispatch } from "react-redux"
import Spinners from "components/Shared/Spinners"
import { showAlert } from "store/reducers/alertReducer"
import {
  getGlobalSettingByType,
  updateGlobalSettingByType,
} from "store/actions/globalSettingAction"
import isPermissionEnabled from "common/isPermissionEnabled"
import { useTranslation } from "react-i18next"
import TextError from "components/Shared/TextError"

const FALLBACK_MIN = 10
const FALLBACK_MAX = 5000
const FALLBACK_DENOMS = [30, 50, 100, 150]

const sortDenoms = values => {
  const seen = new Set()
  return (Array.isArray(values) ? values : [])
    .map(value => Number(value))
    .filter(value => Number.isFinite(value) && value > 0)
    .sort((left, right) => left - right)
    .filter(value => {
      if (seen.has(value)) return false
      seen.add(value)
      return true
    })
}

const initialValues = {
  minWalletTxnAmount: FALLBACK_MIN,
  maxWalletTxnAmount: FALLBACK_MAX,
  walletTxnAmountDenoms: FALLBACK_DENOMS,
}

const WalletSettings = () => {
  const { t } = useTranslation()

  document.title = t("Wallet Settings")

  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { loading, isLoading, walletInfo } = useSelector(
    state => state.GlobalSettings
  )
  const { userRole, accountType } = useSelector(state => state.Login)
  const [usersPermissions, setUsersPermissions] = useState({
    list: null,
    edit: null,
  })
  const [formValues, setFormValues] = useState(initialValues)
  const [draftAmount, setDraftAmount] = useState("")
  const [draftError, setDraftError] = useState("")

  useEffect(() => {
    const isPermissionList = isPermissionEnabled(
      userRole,
      60,
      "wallet_settings_1",
      accountType
    )
    const isPermissionEdit = isPermissionEnabled(
      userRole,
      60,
      "wallet_settings_3",
      accountType
    )
    if (!isPermissionList) {
      navigate("/dashboard")
    }
    setUsersPermissions({
      list: isPermissionList,
      edit: isPermissionEdit,
    })
  }, [userRole, accountType, navigate])

  useEffect(() => {
    dispatch(getGlobalSettingByType({ type: "walletInfo" }))
  }, [dispatch])

  useEffect(() => {
    const stored = walletInfo?.walletInfo
    if (!stored) return

    const storedDenoms = stored.walletTxnAmountDenoms
    setFormValues({
      minWalletTxnAmount: stored.minWalletTxnAmount ?? FALLBACK_MIN,
      maxWalletTxnAmount: stored.maxWalletTxnAmount ?? FALLBACK_MAX,
      walletTxnAmountDenoms: Array.isArray(storedDenoms)
        ? sortDenoms(storedDenoms)
        : FALLBACK_DENOMS,
    })
  }, [walletInfo])

  const handleOnSubmit = values => {
    const payload = {
      id: walletInfo?._id,
      type: "walletInfo",
      walletInfo: {
        minWalletTxnAmount: Number(values.minWalletTxnAmount),
        maxWalletTxnAmount: Number(values.maxWalletTxnAmount),
        walletTxnAmountDenoms: sortDenoms(values.walletTxnAmountDenoms),
      },
    }
    dispatch(updateGlobalSettingByType(payload)).then(action => {
      const { statusCode, status, msg } = action?.payload || {}
      if (statusCode === 200 && status) {
        dispatch(showAlert({ text: msg, type: "success" }))
        dispatch(getGlobalSettingByType({ type: "walletInfo" }))
      } else {
        dispatch(showAlert({ text: msg, type: "error" }))
      }
    })
  }

  if (loading) return <Spinners />

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs />
        <Formik
          initialValues={formValues}
          enableReinitialize={true}
          validationSchema={walletSettingsSchema}
          onSubmit={handleOnSubmit}
        >
          {({ values, setFieldValue }) => {
            const addAmount = () => {
              const amount = Number(String(draftAmount).trim())
              const min = Number(values.minWalletTxnAmount)
              const max = Number(values.maxWalletTxnAmount)
              if (!Number.isFinite(amount) || amount <= 0) {
                setDraftError(t("Enter a valid amount"))
                return
              }
              if (
                Number.isFinite(min) &&
                Number.isFinite(max) &&
                (amount < min || amount > max)
              ) {
                setDraftError(t("Amount must be between minimum and maximum"))
                return
              }
              if (
                (values.walletTxnAmountDenoms || []).some(
                  entry => Number(entry) === amount
                )
              ) {
                setDraftError(t("This amount already exists"))
                return
              }
              setFieldValue(
                "walletTxnAmountDenoms",
                sortDenoms([...(values.walletTxnAmountDenoms || []), amount])
              )
              setDraftAmount("")
              setDraftError("")
            }

            const removeAmount = amount => {
              setFieldValue(
                "walletTxnAmountDenoms",
                sortDenoms(
                  (values.walletTxnAmountDenoms || []).filter(
                    entry => Number(entry) !== Number(amount)
                  )
                )
              )
            }

            return (
              <Row>
                <Col lg={6}>
                  <Card>
                    <CardBody>
                      <CardTitle>{t("Wallet Settings")}</CardTitle>
                      <Form>
                        <Row className="mb-3">
                          <Col md={12}>
                            <div className="form_field">
                              <Label htmlFor="minWalletTxnAmount" className="col col-form-label">
                                <span className="text-danger">*</span>
                                {t("Minimum Wallet Transaction Amount")}
                              </Label>
                              <Field
                                name="minWalletTxnAmount"
                                id="minWalletTxnAmount"
                                type="text"
                                inputMode="decimal"
                                className="form-control"
                                placeholder={t("Minimum Wallet Transaction Amount")}
                                disabled={!usersPermissions.edit}
                              />
                              <TextError name="minWalletTxnAmount" />
                            </div>
                          </Col>
                          <Col md={12}>
                            <div className="form_field">
                              <Label htmlFor="maxWalletTxnAmount" className="col col-form-label">
                                <span className="text-danger">*</span>
                                {t("Maximum Wallet Transaction Amount")}
                              </Label>
                              <Field
                                name="maxWalletTxnAmount"
                                id="maxWalletTxnAmount"
                                type="text"
                                inputMode="decimal"
                                className="form-control"
                                placeholder={t("Maximum Wallet Transaction Amount")}
                                disabled={!usersPermissions.edit}
                              />
                              <TextError name="maxWalletTxnAmount" />
                            </div>
                          </Col>
                          <Col md={12}>
                            <div className="form_field">
                              <Label htmlFor="walletTxnAmount" className="col col-form-label">
                                {t("Wallet Amount Options")}
                              </Label>
                              <div className="d-flex gap-2">
                                <Input
                                  id="walletTxnAmount"
                                  type="text"
                                  inputMode="decimal"
                                  value={draftAmount}
                                  placeholder={t("Amount")}
                                  disabled={!usersPermissions.edit}
                                  onChange={event => {
                                    setDraftAmount(event.target.value)
                                    if (draftError) setDraftError("")
                                  }}
                                  onKeyDown={event => {
                                    if (event.key !== "Enter") return
                                    event.preventDefault()
                                    if (usersPermissions.edit) addAmount()
                                  }}
                                />
                                {usersPermissions.edit ? (
                                  <Button type="button" color="primary" onClick={addAmount}>
                                    {t("Create")}
                                  </Button>
                                ) : null}
                              </div>
                              {draftError ? (
                                <div className="text-danger mt-1">{draftError}</div>
                              ) : null}
                              <ErrorMessage name="walletTxnAmountDenoms" component="div" className="text-danger mt-1" />
                              <div className="d-flex flex-wrap gap-2 mt-3">
                                {sortDenoms(values.walletTxnAmountDenoms).map(amount => (
                                  <span
                                    key={amount}
                                    className="badge bg-primary d-inline-flex align-items-center"
                                  >
                                    {amount}
                                    {usersPermissions.edit ? (
                                      <button
                                        type="button"
                                        className="btn btn-sm p-0 text-white ms-2 lh-1"
                                        aria-label={t("Remove")}
                                        onClick={() => removeAmount(amount)}
                                      >
                                        <i className="bx bx-x font-size-16" />
                                      </button>
                                    ) : null}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </Col>
                          <Col lg={12}>
                            <div className="d-flex justify-content-end mt-3">
                              <Button type="button" color="secondary" onClick={() => navigate(-1)}>
                                {t("Cancel")}
                              </Button>
                              {usersPermissions.edit ? (
                                <Button
                                  className="mx-2"
                                  type="submit"
                                  color="success"
                                  disabled={isLoading}
                                >
                                  {t("Save")}
                                </Button>
                              ) : null}
                            </div>
                          </Col>
                        </Row>
                      </Form>
                    </CardBody>
                  </Card>
                </Col>
                <Col lg={6}>
                  <Card className="card_100_24">
                    <CardBody>
                      <CardTitle>{t("Wallet Settings Hints/Notes")}</CardTitle>
                      <div className="hints mt-3">
                        <div className="single_hint">
                          <p className="fw-bold">
                            1. {t("Minimum Wallet Transaction Amount")}
                          </p>
                          <p>
                            {t(
                              "The smallest amount a customer can add to the wallet in one card payment."
                            )}
                          </p>
                        </div>
                        <div className="single_hint">
                          <p className="fw-bold">
                            2. {t("Maximum Wallet Transaction Amount")}
                          </p>
                          <p>
                            {t(
                              "The largest amount a customer can add to the wallet in one card payment."
                            )}
                          </p>
                        </div>
                        <div className="single_hint">
                          <p className="fw-bold">3. {t("Wallet Amount Options")}</p>
                          <p>
                            {t(
                              "These preset amounts are shown when a customer adds wallet credit."
                            )}
                          </p>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            )
          }}
        </Formik>
      </div>
    </div>
  )
}

export default WalletSettings
